/**
 * TeamUsagePage.jsx — /meu-consumo/equipe (C3 visao EQUIPE)
 * 
 * Pagina detalhe do consumo da EQUIPE de um treinador.
 * Consome GET /api/me/usage/team via usageTeamService.
 *
 * UI_PADRAO compliant (mesmo padrao da MyUsagePage):
 *   - Header com back link + titulo + refresh
 *   - Card grande: interacoes do mes (sem cota — equipe nao tem cota propria)
 *   - Cards: tokens + participacao na cota do clube (%, sem USD — UI_PADRAO)
 *   - Tabela "Por atleta"
 *   - Tabela "Por assistente"
 *   - Card contexto: "Y atletas vinculados, X ativos este mes"
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, Zap, Bot, Users, Info,
  ArrowLeft, RefreshCw, AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSportsLang } from '../contexts/SportsLangContext';
import { getTeamUsage, invalidateTeamUsageCache } from '../services/usageTeamService';

const I18N = {
  pt: {
    back: 'Voltar',
    title: 'Consumo da equipe',
    subtitle: 'Detalhamento das interações dos seus atletas no mês corrente',
    refresh: 'Atualizar',
    refreshing: 'Atualizando...',
    loading: 'Carregando consumo da equipe...',
    forbidden: 'Esta página é restrita a usuários com role TREINADOR.',
    errorTitle: 'Não foi possível carregar',
    errorRetry: 'Tentar novamente',
    cardInteractions: 'Interações',
    cardInteractionsHelp: 'Total de chamadas dos seus atletas a assistentes',
    cardTokens: 'Tokens',
    cardTokensHelp: 'Soma de tokens processados',
    cardCost: 'Participação na cota do clube',
    cardCostHelp: 'Quanto da cota mensal de créditos do clube foi usado pela sua equipe',
    cardCostNoSub: 'Clube sem assinatura ativa — participação não calculada',
    byAthleteTitle: 'Por atleta',
    byAthleteEmpty: 'Nenhum atleta consumiu assistentes neste mês.',
    byAgentTitle: 'Por assistente',
    byAgentEmpty: 'Nenhum assistente usado pelos atletas neste mês.',
    colAthlete: 'Atleta',
    colAgent: 'Assistente',
    colInteractions: 'Interações',
    colTokens: 'Tokens',
    colCost: '% da cota',
    contextTitle: 'Sua equipe',
    contextLine: (total, active) =>
      `${total} atletas vinculados — ${active} consumiram assistentes este mês`,
    contextHonest:
      'Este consumo é contado dentro da cota total do clube. Não há cota separada por equipe — fale com a pessoa GESTORA para acompanhar o uso do clube como um todo.',
    monthLabel: 'Período de referência',
    asOf: 'Atualizado em',
  },
  en: {
    back: 'Back',
    title: 'Team usage',
    subtitle: 'Breakdown of your athletes\' interactions this month',
    refresh: 'Refresh',
    refreshing: 'Refreshing...',
    loading: 'Loading team usage...',
    forbidden: 'This page is restricted to users with TREINADOR role.',
    errorTitle: 'Could not load',
    errorRetry: 'Try again',
    cardInteractions: 'Interactions',
    cardInteractionsHelp: 'Total athlete calls to assistants',
    cardTokens: 'Tokens',
    cardTokensHelp: 'Sum of tokens processed',
    cardCost: 'Share of club quota',
    cardCostHelp: 'How much of the club\'s monthly credit quota your team used',
    cardCostNoSub: 'Club has no active subscription — share not computed',
    byAthleteTitle: 'By athlete',
    byAthleteEmpty: 'No athlete used assistants this month.',
    byAgentTitle: 'By assistant',
    byAgentEmpty: 'No assistant used by athletes this month.',
    colAthlete: 'Athlete',
    colAgent: 'Assistant',
    colInteractions: 'Interactions',
    colTokens: 'Tokens',
    colCost: '% of quota',
    contextTitle: 'Your team',
    contextLine: (total, active) =>
      `${total} athletes linked — ${active} used assistants this month`,
    contextHonest:
      'This usage counts toward the total club quota. There is no separate team quota — ask the club MANAGER to track overall usage.',
    monthLabel: 'Reference period',
    asOf: 'Updated at',
  },
};

const fmtNumber = (n, lang) =>
  n == null ? '—' : Number(n).toLocaleString(lang === 'en' ? 'en-US' : 'pt-BR');

// UI_PADRAO: consumo exibido como % da cota do clube (créditos = interações).
// Sem cor de alerta — quota alta é informação, não emergência.
const fmtPct = (used, limit, lang) => {
  if (!limit || Number(limit) <= 0) return '—';
  const pct = (Number(used || 0) / Number(limit)) * 100;
  if (pct > 0 && pct < 1) return '<1%';
  return `${pct.toLocaleString(lang === 'en' ? 'en-US' : 'pt-BR', { maximumFractionDigits: 1 })}%`;
};

const fmtAgent = (slug) => {
  if (!slug) return '—';
  return slug.split('_').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
};

const fmtTimestamp = (iso, lang) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(lang === 'en' ? 'en-US' : 'pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const TeamUsagePage = () => {
  const { user } = useAuth();
  const { lang } = useSportsLang();
  const t = I18N[lang] || I18N.pt;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);

  const load = useCallback(async ({ force = false } = {}) => {
    if (force) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const res = await getTeamUsage({ force });
      if (!res) { setForbidden(true); setData(null); }
      else { setData(res); setForbidden(false); setFetchedAt(new Date().toISOString()); }
    } catch (err) {
      console.error('[TeamUsagePage] erro:', err);
      setError(err?.response?.data?.error || err?.message || 'unknown_error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { if (user?.id) load(); }, [user, load]);

  const handleRefresh = () => { invalidateTeamUsageCache(); load({ force: true }); };

  if (loading && !data && !forbidden) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-64 bg-gray-200 rounded" />
            <div className="h-4 w-96 bg-gray-200 rounded" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => <div key={i} className="h-32 bg-white border border-gray-200 rounded-lg" />)}
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-6 text-center">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <Link to="/feed" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 font-medium">
            <ArrowLeft size={16} /> {t.back}
          </Link>
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <Users size={40} className="mx-auto mb-4 text-gray-400" />
            <p className="text-sm text-gray-600">{t.forbidden}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <Link to="/feed" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 font-medium">
            <ArrowLeft size={16} /> {t.back}
          </Link>
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <AlertTriangle size={40} className="mx-auto mb-4" style={{ color: '#a93333' }} />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">{t.errorTitle}</h2>
            <p className="text-sm text-gray-600 mb-6">{String(error)}</p>
            <button onClick={() => load({ force: true })} className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-md text-sm font-medium hover:bg-gray-800">
              <RefreshCw size={14} /> {t.errorRetry}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { team, usage_current_month: usage, subscription } = data;
  const interactions = usage?.interactions || 0;
  const tokens = usage?.tokens || 0;
  const quotaLimit = subscription?.ai_credits_monthly || 0;
  const quotaPct = quotaLimit > 0 ? Math.min(100, (interactions / quotaLimit) * 100) : 0;
  const byAthlete = Array.isArray(usage?.by_athlete) ? usage.by_athlete : [];
  const byAgent = Array.isArray(usage?.by_agent) ? usage.by_agent : [];
  const period = usage?.period_start ? fmtTimestamp(usage.period_start, lang).split(',')[0] : '—';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link to="/feed" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 font-medium">
          <ArrowLeft size={16} /> {t.back}
        </Link>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-gray-900 mb-1">{t.title}</h1>
            <p className="text-sm text-gray-600">{t.subtitle}</p>
            <p className="text-xs text-gray-500 mt-2">
              {t.monthLabel}: <span className="font-medium">{period}</span>
              {fetchedAt && (<span className="ml-3 text-gray-400">· {t.asOf} {fmtTimestamp(fetchedAt, lang)}</span>)}
            </p>
          </div>
          <button onClick={handleRefresh} disabled={refreshing}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-50 transition-colors flex-shrink-0">
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? t.refreshing : t.refresh}
          </button>
        </div>

        {/* Metricas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="md:col-span-3 bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Activity size={22} className="text-gray-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t.cardInteractions}</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">{fmtNumber(interactions, lang)}</p>
                <p className="text-xs text-gray-500 mt-2">{t.cardInteractionsHelp}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Zap size={18} className="text-gray-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t.cardTokens}</p>
                <p className="text-2xl font-bold text-gray-900">{fmtNumber(tokens, lang)}</p>
                <p className="text-xs text-gray-500 mt-2">{t.cardTokensHelp}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-5 md:col-span-2">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Activity size={18} className="text-gray-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t.cardCost}</p>
                <p className="text-2xl font-bold text-gray-900">{fmtPct(interactions, quotaLimit, lang)}</p>
                {quotaLimit > 0 ? (
                  <>
                    <div className="w-full h-1.5 bg-gray-200 rounded-full mt-3 overflow-hidden">
                      <div className="h-full bg-gray-700 rounded-full transition-all" style={{ width: `${quotaPct}%` }} />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">{t.cardCostHelp}</p>
                  </>
                ) : (
                  <p className="text-xs text-gray-500 mt-2">{t.cardCostNoSub}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Por atleta */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">{t.byAthleteTitle}</h2>
          </div>
          {byAthlete.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <Users size={32} className="mx-auto mb-3 text-gray-400" />
              <p className="text-sm text-gray-500">{t.byAthleteEmpty}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{t.colAthlete}</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">{t.colInteractions}</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">{t.colTokens}</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">{t.colCost}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {byAthlete.map((row) => (
                    <tr key={row.athlete_user_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3 font-medium text-gray-900">{row.athlete_name || '—'}</td>
                      <td className="px-6 py-3 text-right font-mono text-gray-900">{fmtNumber(row.interactions, lang)}</td>
                      <td className="px-6 py-3 text-right font-mono text-gray-700">{fmtNumber(row.tokens, lang)}</td>
                      <td className="px-6 py-3 text-right font-mono text-gray-700">{fmtPct(row.interactions, quotaLimit, lang)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Por agente */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">{t.byAgentTitle}</h2>
          </div>
          {byAgent.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <Bot size={32} className="mx-auto mb-3 text-gray-400" />
              <p className="text-sm text-gray-500">{t.byAgentEmpty}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{t.colAgent}</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">{t.colInteractions}</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">{t.colTokens}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {byAgent.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2">
                          <Bot size={14} className="text-gray-400" />
                          <span className="font-medium text-gray-900">{fmtAgent(row.agent_name)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-3 text-right font-mono text-gray-900">{fmtNumber(row.interactions, lang)}</td>
                      <td className="px-6 py-3 text-right font-mono text-gray-700">{fmtNumber(row.tokens, lang)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Contexto */}
        <div className="bg-gray-100 border border-gray-200 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-md bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
              <Info size={16} className="text-gray-600" />
            </div>
            <div className="flex-1 min-w-0 text-sm text-gray-700">
              <p className="font-semibold text-gray-900 mb-2">{t.contextTitle}</p>
              <p className="mb-3">{t.contextLine(team?.athletes_total ?? 0, team?.athletes_active ?? 0)}</p>
              <p className="text-gray-600 leading-relaxed">{t.contextHonest}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamUsagePage;
